// Display-only device types.
//
// The edge agent classifies every Modbus-only device as role "PLC" (ai_sentry_agent
// network_monitor.py _infer_asset_role), and that value is stored as ot_assets.asset_type and
// joined into each event as details.dest_role. Enabled policies match on it
// (e.g. `dest_role == "PLC"` in "Detect PLC Enumeration Scans"), so the STORED value must not
// change: changing it would change what those policies match.
//
// This table therefore changes only what the UI SHOWS. A device is recognised by its identity
// (vendor + model, as the Modbus FC43 device identity reports it, e.g. "SEL SEL-751"), never by
// its stored role. Each entry names a role class from the engine's role vocabulary
// (ProtectionRelay, RTU, SubstationGateway — values _infer_substation_role already emits; and
// Historian, added here for display only, which the engine never emits) and the label shown.
// Anything not listed keeps its existing label.

export const DEVICE_TYPES = [
  { pattern: /\bSEL-751\b/i,             roleClass: 'ProtectionRelay',   label: 'Feeder protection relay' },
  { pattern: /\bSEL-411L\b/i,            roleClass: 'ProtectionRelay',   label: 'Line protection relay' },
  { pattern: /\bGE\b.*\bL90\b/i,         roleClass: 'ProtectionRelay',   label: 'Line differential relay' },
  { pattern: /\bREL670\b/i,              roleClass: 'ProtectionRelay',   label: 'Distance relay' },
  { pattern: /\bSICAM[- ]?A8000\b/i,     roleClass: 'RTU',               label: 'RTU' },
  { pattern: /\bMGate\b/i,               roleClass: 'SubstationGateway', label: 'Protocol gateway' },
  { pattern: /\bOSIsoft\b.*\bPI\b/i,     roleClass: 'Historian',         label: 'Historian' },
];

// Two KNOWN_DEVICES entries (constants/topology.js, which the dark /topology page owns and which is not edited
// here) are named for something other than the device behind them: 10.x.1.76 is called "SCADA Master" but is
// the OSIsoft PI historian, 10.x.1.75 "IEC 61850 Gateway" but is a Moxa MGate. The pages that show device
// types show these entries under the names the devices register with (asset_name), so name and type agree.
// Keyed by the KNOWN_DEVICES IP, and applied ONLY where a KNOWN_DEVICES name is read — never to a registered
// asset's own name (a real asset named "SCADA Master" elsewhere keeps its name).
export const KNOWN_NAME_OVERRIDES = {
  '10.10.1.75': 'Moxa MGate-5217',   '10.10.1.76': 'OSIsoft PI-Server',
  '10.20.1.75': 'Moxa MGate-5217',   '10.20.1.76': 'OSIsoft PI-Server',
  '10.30.1.75': 'Moxa MGate-5217',   '10.30.1.76': 'OSIsoft PI-Server',
};

// The name to show for the KNOWN_DEVICES entry at `ip` whose name is `knownName`: the override above, else
// `knownName`. A missing entry (no name) stays missing.
export function knownDeviceName(ip, knownName) {
  return (knownName && KNOWN_NAME_OVERRIDES[ip]) || knownName;
}

// The entry for a device, or null. `name` and `vendor` are the asset's asset_name and vendor
// (or a KNOWN_DEVICES entry's name and vendor).
export function resolveDeviceType(name, vendor) {
  const identity = `${vendor || ''} ${name || ''}`.trim();
  if (!identity) return null;
  return DEVICE_TYPES.find(t => t.pattern.test(identity)) || null;
}

// The label to show for a device: the mapped label, else `fallback` (the role the page showed before).
export function deviceTypeLabel(name, vendor, fallback) {
  const t = resolveDeviceType(name, vendor);
  return t ? t.label : fallback;
}

// The role class to style a device by (colour, icon): the mapped class, else `fallback`.
export function deviceRoleClass(name, vendor, fallback) {
  const t = resolveDeviceType(name, vendor);
  return t ? t.roleClass : fallback;
}
